/* eslint-disable react/no-array-index-key */
/* eslint-disable no-param-reassign */
/* eslint-disable max-len */
import { useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { getOneCase } from '@brands/services/cases/getCaseById';
import { CaseType } from '@brands/services/cases/types/ICase';
import { getConversation } from '@brands/services/communication/getConversation';
import { IConversationStatistics } from '@brands/services/communication/listConversationStatistics';
import { IGMConversation } from '@brands/services/communication/types/IGMConversation';
import { IGMConversationParticipant } from '@brands/services/communication/types/IGMConversationParticipant';
import { getUserAvatar } from '@brands/services/identity/getUserAvatar';
import { UserProfile, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { getCaseDateWithoutDay, getCaseTime, getTimeAgo } from '@brands/Utils/formatDate';
import { getFriendlyNameOfCaseType } from '@brands/Utils/getFriendlyName';
import { shouldShowDoctorTitle } from '@brands/Utils/getProviderTitle';
import linkifyHtml from 'linkify-html';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import sanitizeHtml from 'sanitize-html';

import styles from './case-styles.module.scss';
import { getFriendlyRoleName } from '@brands/Utils/getFriendlyName';

interface IModal {
  conversation: IConversationStatistics;
  isSelected: boolean;
  isMobile?: boolean;
  setSelectedConversationParticipants: React.Dispatch<React.SetStateAction<IGMConversationParticipant[]>>;
  setSelectedConversationForMessageBody: React.Dispatch<React.SetStateAction<IGMConversation | undefined>>;
}

const CaseMessageInfo = ({
  conversation,
  isSelected,
  isMobile,
  setSelectedConversationParticipants,
  setSelectedConversationForMessageBody,
}: IModal): JSX.Element => {
  const { userInfo } = useAppSelector(selectAuth);
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const roleNavigate = useRoleNavigate();
  const [item, setItem] = useState<IGMConversation>();
  const [remoteParticipant, setRemoteParticipant] = useState<IGMConversationParticipant[]>();

  const unreadMessageIndicator = useMemo(() => {
    return conversation?.unread_message_count > 0 ? conversation.unread_message_count : 0;
  }, [conversation]);

  const unreadCount = unreadMessageIndicator;

  useEffect(() => {
    let isMounted = true;

    const fetchConversation = async (): Promise<void> => {
      if (!conversation?.conversation_sid || item) return;

      try {
        const currentConversation = await getConversation(conversation.conversation_sid);

        if (isMounted) {
          setItem(currentConversation);

          // Header lists active participants. A care advisor reassigned off
          // the case stays on the conversation as an `inactive` participant
          // and would otherwise show greyed out in place of the current CA.
          // Fall back to all remotes when none are active (e.g. an archived
          // thread or a participant who left) so the header never renders
          // empty. Message bubbles keep the full participant list, so
          // previous CAs remain attributable when scrolling the thread.
          // A dependent's own participant row sits next to their guardian's,
          // so leave it out the way the case-chat list does; otherwise a
          // guardian sees their dependent as a second party on the thread.
          const isRemote = (participant: IGMConversationParticipant): boolean =>
            Number(participant.identity) !== Number(userInfo.id) && participant.role !== UserRoleName.Dependent;
          const activeRemote =
            currentConversation.participants?.filter(
              (participant) => isRemote(participant) && participant.status === 'active',
            ) ?? [];
          const remote = (
            activeRemote.length > 0 ? activeRemote : currentConversation.participants?.filter(isRemote) ?? []
          ) as IGMConversationParticipant[];

          setRemoteParticipant(remote || []);
        }
      } catch (error) {
        console.error('Failed to fetch conversation:', error);
      }
    };

    fetchConversation();

    return () => {
      isMounted = false;
    };
  }, [conversation?.conversation_sid, item, userInfo.id]);

  const isUserParticipant = item?.participants?.some(
    (participant) => Number(participant.identity) === Number(userInfo.id),
  );

  // Patients don't need the Case ID; every staff role uses it to tie the
  // thread back to its case.
  const showCaseId =
    userInfo.role.name !== UserRoleName.Patient &&
    userInfo.role.name !== UserRoleName.Dependent &&
    !!conversation.case_id;

  // Dependent cases store the guardian as the conversation participant, so
  // we swap in the case patient to make the card identify the dependent.
  type DisplayParticipant = Pick<IGMConversationParticipant, 'identity' | 'full_name' | 'role' | 'status'>;
  const displayParticipants: DisplayParticipant[] = useMemo(() => {
    const dependentRow = (status: string): DisplayParticipant => ({
      identity: String(conversation.patient_id),
      full_name: conversation.patient_full_name ?? '',
      role: UserRoleName.Patient,
      status,
    });
    const swapped = (remoteParticipant ?? []).map((p) =>
      p.role === UserRoleName.Patient &&
      conversation.patient_id !== null &&
      Number(p.identity) !== Number(conversation.patient_id)
        ? dependentRow(p.status)
        : { identity: p.identity, full_name: p.full_name, role: p.role, status: p.status },
    );
    if (
      swapped.length === 0 &&
      conversation.patient_id !== null &&
      Number(conversation.patient_id) !== Number(userInfo.id)
    ) {
      return [dependentRow('active')];
    }
    return swapped;
  }, [remoteParticipant, conversation.patient_id, conversation.patient_full_name, userInfo.id]);

  // The conversation payload carries only a participant's name and role, so
  // the provider's credentials come from the case, matching the open thread.
  const hasProviderParticipant = displayParticipants.some((p) => p.role === UserRoleName.Provider);
  const [caseProvider, setCaseProvider] = useState<UserProfile>();

  useEffect(() => {
    if (!hasProviderParticipant || !conversation.case_id) return undefined;
    let isMounted = true;

    getOneCase(conversation.case_id, undefined, userInfo.role.name)
      .then((currentCase) => {
        if (isMounted) setCaseProvider(currentCase.provider);
      })
      .catch((error) => {
        console.error('Failed to fetch case for provider title:', error);
      });

    return () => {
      isMounted = false;
    };
  }, [hasProviderParticipant, conversation.case_id, userInfo.role.name]);

  // Matched by id so a provider who has since left the case isn't titled
  // from the current provider's credentials.
  const showDoctorTitle = (participant: DisplayParticipant): boolean =>
    participant.role === UserRoleName.Provider &&
    Number(caseProvider?.id) === Number(participant.identity) &&
    shouldShowDoctorTitle(caseProvider?.provider_profile?.credentials);

  const isSenderYou = Number(item?.last_message?.created_by) === Number(userInfo.id);
  const senderName = isSenderYou ? 'You' : null;

  const rawHtmlWithSender = senderName
    ? `${senderName}: ${item?.last_message?.body || ''}`
    : item?.last_message?.body || '';

  const withLinks = linkifyHtml(rawHtmlWithSender, {
    defaultProtocol: 'https',
    attributes: {
      rel: 'noopener noreferrer',
      target: '_blank',
    },
  });

  const sanitizedHtml = sanitizeHtml(withLinks, {
    allowedTags: ['p', 'strong', 'em', 'u', 'ol', 'ul', 'li', 'br', 'span', 'a'],
    allowedAttributes: {
      a: ['href', 'target', 'rel'],
      span: ['class'],
      li: ['data-list'],
    },
    allowedClasses: {
      span: ['ql-ui'],
    },
    transformTags: {
      a: (tagName, attribs) => ({
        tagName: 'a',
        attribs: {
          ...attribs,
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
    },
  });

  const routeConversationSid = useParams().conversationSid || null;
  useEffect(() => {
    if (conversation.conversation_sid === routeConversationSid && remoteParticipant && item) {
      setSelectedConversationParticipants(remoteParticipant as IGMConversationParticipant[]);
      setSelectedConversationForMessageBody(item);
    }
  }, [conversation, remoteParticipant, item, routeConversationSid]);

  return (
    <div
      className={`${styles.participantDiv} ${styles.participantMessageDiv}`}
      onClick={() => {
        roleNavigate(`/messages/${conversation?.conversation_sid}`);
      }}
    >
      <div className={styles.wrapDiv}>
        {displayParticipants.map((remoteParticipantSingle) => (
          <div
            className={`${styles.participantDivInfo} ${
              remoteParticipantSingle.status.toLowerCase() !== 'active' ? styles.inactiveParticipant : ''
            }`}
          >
            <div className={styles.participantDivInfoLeft}>
              <span className={`${styles.profileImageDate} ${styles.imageContainer}`}>
                {unreadCount > 0 &&
                  Number(remoteParticipantSingle.identity) === Number(item?.last_message?.created_by) && (
                    <span className={styles.unreasMessage} />
                  )}
                <img
                  alt="Avatar"
                  onError={(event: any) => {
                    event.target.src =
                      'https://media.istockphoto.com/id/1130884625/tr/vekt%C3%B6r/kullan%C4%B1c%C4%B1-%C3%BCyesi-vekt%C3%B6r-simgesi-ui-kullan%C4%B1c%C4%B1-arabirimi-veya-profil-face-avatar-uygulamas%C4%B1.jpg?s=612x612&w=0&k=20&c=jAf5nq1ebnZo8TJbjaB9dMHMvgff7uOk67NkF5CpgB0=';
                    event.target.onError = null;
                  }}
                  className={`fs.exclude ${styles.profileImage}`}
                  src={getUserAvatar(Number(remoteParticipantSingle.identity))}
                />
              </span>
              <div className={`${styles.details} ${isMobile && styles.mobileCaseMessageInfo}`}>
                <div className={styles.detailsNameImage}>
                  <span className={`${styles.participantName} fs.exclude ${!isSelected ? styles.primaryColor : ''}`}>
                    {showDoctorTitle(remoteParticipantSingle) && <span style={{ paddingRight: '4px' }}>Dr.</span>}
                    {remoteParticipantSingle.full_name}{' '}
                    {remoteParticipantSingle.role !== UserRoleName.Dependent &&
                      remoteParticipantSingle?.status === 'Deactivated' &&
                      '(Deactivated)'}
                  </span>
                </div>

                {isUserParticipant && (
                  <span className={`fs-unmask ${styles.conversationType}`}>
                    {getFriendlyRoleName(remoteParticipantSingle.role)}
                  </span>
                )}
              </div>
            </div>
            <div className={styles.participantDivInfoRight}>
              <span className={styles.conversationSpecificInfoLastMessageTime}>
                {getTimeAgo(
                  item?.last_message?.updated_at
                    ? item?.last_message?.updated_at
                    : (item?.last_message?.created_at as string),
                  timeZone,
                )}
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className={styles.conversationSpecificInfo}>
        {showCaseId && <span className={`fs-unmask ${styles.caseIdChip}`}>Case ID: {conversation.case_id}</span>}
        <span className={styles.conversationSpecificInfoCaseInfo}>
          {getFriendlyNameOfCaseType(conversation.case_type)}
          {(conversation.case_type === CaseType.video_call_scheduled ||
            conversation.case_type === CaseType.video_call_instant ||
            conversation.case_type === CaseType.phone_call_scheduled) &&
            conversation.case_video_conference_date !== null && (
              <span className={`fs-unmask ${styles.currentCaseVideoDate}`}>
                <span> on {getCaseDateWithoutDay(conversation.case_video_conference_date, timeZone)}</span>
                <span> at {getCaseTime(conversation.case_video_conference_date, timeZone)}</span>
              </span>
            )}
        </span>
        <div className={styles.chatInfoFooter}>
          <div className={styles.chatInfoFooterLastMessage}>
            {item?.last_message?.body ? (
              <span
                className={`${styles.conversationSpecificInfoLastMessage} fs.exclude`}
                dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
              />
            ) : item?.last_message?.media_ids && item?.last_message?.media_ids.length > 0 ? (
              senderName === 'You' ? (
                `${senderName}: Media`
              ) : (
                'Media'
              )
            ) : item?.last_message?.body === null && item?.last_message?.updated_at !== null ? (
              senderName === 'You' ? (
                `${senderName}: This message was deleted`
              ) : (
                'This message was deleted'
              )
            ) : (
              ''
            )}
          </div>
          {conversation &&
            unreadCount > 0 &&
            [UserRoleName.Patient, UserRoleName.Provider, UserRoleName.Collector].includes(
              userInfo.role.name as UserRoleName,
            ) && <div className={styles.notificationUnreadCountPatient}>{unreadCount}</div>}
        </div>
      </div>
    </div>
  );
};

CaseMessageInfo.defaultProps = {
  isMobile: false,
};
export default CaseMessageInfo;
